/**
 * @module Page-Mobile-Web-Development
 */

import { BANNER_TYPE } from "../../../globals/constants";
import { useEffect } from "react";
import { loadScript } from "../../../globals/common-functions";
import banner from "../../../globals/banner.json";
import AvertImage from "../../../globals/elements/avertimg";
import SectionInnerPageBanner from "../../sections/common/banner";

function MobileWebDevelopmentPage() {

    useEffect(()=>{
        loadScript("js/custom.js");
    })

    return (

        <div className="av-sv2-wrap">
            <SectionInnerPageBanner _data={banner.service_detail} _type={BANNER_TYPE.COMPACT} />

            {/* Hero */}
            <div className="section-full p-t120 p-b0 site-bg-white">
                <div className="container">
                    <div className="av-hero-v2">
                        <div className="row align-items-center">
                            <div className="col-lg-7">
                                <div className="av-hero-eyebrow">
                                    <i className="feather feather-smartphone"></i>
                                    Apps & Websites
                                </div>
                                <h1>Mobile & Web Development</h1>
                                <p className="av-hero-lead">
                                    Your customers are on their phones and online — that's where you need to be too.
                                    We build mobile apps and websites that people genuinely enjoy using, not just tolerate.
                                </p>
                            </div>
                            <div className="col-lg-5">
                                <div className="av-hero-visual">
                                    <AvertImage src="images/services/mobile-web-development.jpg" alt="Mobile & Web Development" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Two Services */}
            <div className="av-section-v2 av-bg-slate">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-6 m-b30">
                            <div className="av-card-v2">
                                <div className="av-card-icon">
                                    <i className="feather feather-smartphone"></i>
                                </div>
                                <h3>Mobile App Development</h3>
                                <p>Whether it's an app for your customers or an internal tool for your team, we build mobile experiences that work reliably on both iPhone and Android.</p>
                                <ul>
                                    <li>iOS and Android apps</li>
                                    <li>Cross-platform solutions</li>
                                    <li>Progressive web apps</li>
                                    <li>App store setup and launch</li>
                                    <li>Ongoing maintenance and updates</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-lg-6 m-b30">
                            <div className="av-card-v2">
                                <div className="av-card-icon">
                                    <i className="feather feather-monitor"></i>
                                </div>
                                <h3>Web Development</h3>
                                <p>From business websites to complex web platforms, we build things that look great on every screen size and load fast — because nobody waits around for slow sites.</p>
                                <ul>
                                    <li>Responsive websites</li>
                                    <li>E-commerce platforms</li>
                                    <li>Web applications</li>
                                    <li>Content management systems</li>
                                    <li>Performance optimisation</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Process */}
            <div className="av-section-v2">
                <div className="container">
                    <div className="av-sh">
                        <div className="av-sh-label">Our Approach</div>
                        <h2>How We Build Your Product</h2>
                        <p>We don't disappear for months and come back with something you didn't ask for. You're part of the process from day one.</p>
                    </div>

                    <div className="row av-timeline">
                        <div className="col-lg-4 col-md-6 m-b30">
                            <div className="av-tl-step">
                                <div className="av-tl-num">1</div>
                                <h4>Understanding Your Users</h4>
                                <p>Who's going to use this, and what do they need? We start there — it shapes every decision we make.</p>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 m-b30">
                            <div className="av-tl-step">
                                <div className="av-tl-num">2</div>
                                <h4>Design & Prototype</h4>
                                <p>You'll see and interact with designs before we build. No guessing — you know exactly what you're getting.</p>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 m-b30">
                            <div className="av-tl-step">
                                <div className="av-tl-num">3</div>
                                <h4>Build & Launch</h4>
                                <p>We develop with tested technologies, QA everything thoroughly, and handle the launch so you don't have to.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Dark Panel - What Sets Us Apart + Tech */}
            <div className="av-section-v2">
                <div className="container">
                    <div className="av-panel-dark">
                        <h3>What makes our apps and websites different</h3>
                        <p>We obsess over the details that matter — speed, usability, and reliability. Here's what that looks like in practice.</p>
                        <div className="av-pd-grid">
                            <div className="av-pd-item">
                                <i className="feather feather-zap"></i>
                                <h5>Fast by default</h5>
                                <p>We optimise every build for speed. Your users shouldn't have to wait for anything.</p>
                            </div>
                            <div className="av-pd-item">
                                <i className="feather feather-eye"></i>
                                <h5>Designed for people</h5>
                                <p>Interfaces that feel natural and intuitive, not ones that need a manual to figure out.</p>
                            </div>
                            <div className="av-pd-item">
                                <i className="feather feather-lock"></i>
                                <h5>Secure from the start</h5>
                                <p>Security is built in from day one, not added as an afterthought before launch.</p>
                            </div>
                            <div className="av-pd-item">
                                <i className="feather feather-maximize-2"></i>
                                <h5>Ready to scale</h5>
                                <p>Whether you have 100 users or 100,000 — your product will handle the growth.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Technologies */}
            <div className="av-section-v2 av-bg-slate">
                <div className="container">
                    <div className="av-sh">
                        <div className="av-sh-label">Our Stack</div>
                        <h2>Technologies We Work With</h2>
                        <p>We pick the right tool for the job — not just whatever's trendy.</p>
                    </div>
                    <div className="av-pill-grid" style={{justifyContent: 'center'}}>
                        <span className="av-pill">React & React Native</span>
                        <span className="av-pill">Flutter</span>
                        <span className="av-pill">TypeScript</span>
                        <span className="av-pill">Node.js</span>
                        <span className="av-pill">Laravel & PHP</span>
                        <span className="av-pill">PostgreSQL</span>
                        <span className="av-pill">MySQL</span>
                        <span className="av-pill">MongoDB</span>
                        <span className="av-pill">AWS</span>
                        <span className="av-pill">Next.js</span>
                        <span className="av-pill">Vue.js</span>
                    </div>
                </div>
            </div>

            {/* CTA */}
            <div className="av-section-v2">
                <div className="container">
                    <div className="av-cta-v2">
                        <h3>Have an app or website idea?</h3>
                        <p>We'd love to hear about it. Let's talk through what you need and figure out the best way to bring it to life.</p>
                        <div className="av-cta-actions">
                            <a href="/contact-us" className="av-btn-white">Start a Conversation</a>
                            <a href="/success-stories" className="av-btn-ghost">View Our Work</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default MobileWebDevelopmentPage;
